import { useContext } from 'react';
import AppContext from '../../../AppContext';
import QtrDirectPremiumRowData from './QtrDirectPremiumRowData';
import styles from './QtrDirectPremiumRow.module.css';
import { RatioFormat, DollarFormat } from '../../format';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';
import BlankRow from '../BlankRow/BlankRow';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

interface QtrDirectPremiumRowProps extends QtrDirectPremiumRowData {
  /** Whether to show the Premium % Change column value */
  readonly showPremiumPercentChange?: boolean;
  /** Whether to show the Premium $ Change column value */
  readonly showDollarNumberChange?: boolean;
}

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return 'indent';
    case 'TOTAL':
      return 'total';
    case 'GRAND_TOTAL':
      return 'grand_total';
    case 'BOLD':
      return 'bold';
    default:
      return '';
  }
}

function hasAnyOutsideBenchmarks(data: QtrDirectPremiumRowData) {
  return !!(
    data.premiumPercentChange?.outsideBenchmark ||
    data.premiumDollarChange?.outsideBenchmark ||
    data.cqDirectPremium?.outsideBenchmark ||
    data.cqPercentOfTotal?.outsideBenchmark ||
    data.pyqDirectPremium?.outsideBenchmark ||
    data.pyqPercentOfTotal?.outsideBenchmark
  );
}

function getDataDisplayForCurrentRepository(
  riskRepository: string | null,
  rowDisplay: QtrDirectPremiumRowData['rowDisplay']
) {
  if (riskRepository === 'FR' || !rowDisplay || !riskRepository) {
    return '';
  }
  const displayValue = rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay];
  if (displayValue === 'GREY') return 'grey';
  if (displayValue === 'HIDE') return 'hidden';
  return '';
}

export default function QtrDirectPremiumRow({
  sectionKey,
  subSectionKey,
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  rowDisplay,
  calculateDetail,
  rowDataSource,
  rowLinkConfiguration,
  premiumPercentChange,
  premiumDollarChange,
  cqDirectPremium,
  cqPercentOfTotal,
  pyqDirectPremium,
  pyqPercentOfTotal,
  showPremiumPercentChange = false,
  showDollarNumberChange = false
}: Readonly<QtrDirectPremiumRowProps>) {
  const { showBenchmark, riskRepository } = useContext(AppContext);

  if (label.trim().toLowerCase() === 'missing') {
    return null;
  }

  if (rowType === 'BLANK') {
    return <BlankRow />;
  }

  const repoDisplay = getDataDisplayForCurrentRepository(riskRepository, rowDisplay);

  const className = cx(
    'container',
    { collapse: !showBenchmark },
    repoDisplay,
    rowClassSwitch(rowClass)
  );

  if (rowType === 'LABEL') {
    const id =
      'label-' +
      label
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]+/g, '')
        .toLowerCase();
    return (
      <div id={id} data-testid={id} className={className}>
        <RowHelmet label={label} rowLinkConfiguration={rowLinkConfiguration} />
        <LabelRow />
      </div>
    );
  }

  const data: QtrDirectPremiumRowData = {
    rowKey,
    rowType,
    rowClass,
    label,
    benchmark,
    rowDisplay,
    calculateDetail,
    rowDataSource,
    rowLinkConfiguration,
    premiumPercentChange,
    premiumDollarChange,
    cqDirectPremium,
    cqPercentOfTotal,
    pyqDirectPremium,
    pyqPercentOfTotal
  };

  const id = sectionKey ? `${sectionKey}-${rowKey}` : `rowKey-${rowKey}`;

  return (
    <div id={id} data-testid="qtr-direct-premium-data-row" className={className}>
      <RowHelmet
        label={label}
        rowKey={rowKey}
        isOutsideBenchmark={hasAnyOutsideBenchmarks(data)}
        benchmarkText={benchmark}
        calculateDetail={calculateDetail}
        rowType={rowType}
        rowDataSource={rowDataSource}
        rowLinkConfiguration={rowLinkConfiguration}
        sectionKey={sectionKey}
        subSectionKey={subSectionKey}
      />

      {/* 5: Premium % Change (value only when enabled) */}
      {showPremiumPercentChange ? (
        <RatioFormat {...premiumPercentChange} showDefaultValue={true} />
      ) : (
        <p></p>
      )}

      {/* 6: Premium $ Change (value only when enabled) */}
      {showDollarNumberChange ? (
        <DollarFormat {...premiumDollarChange} showDefaultValue={true} />
      ) : (
        <p></p>
      )}

      {/* 7: Direct Prem (CQ) */}
      <DollarFormat {...cqDirectPremium} />

      {/* 8: % of Total (CQ) */}
      <RatioFormat {...cqPercentOfTotal} />

      {/* 9: Direct Prem (PYQ) */}
      <DollarFormat {...pyqDirectPremium} />

      {/* 10: % of Total (PYQ) */}
      <RatioFormat {...pyqPercentOfTotal} />
    </div>
  );
}
